import React from 'react'
import { OrganizationJsonLd, SiteLinksSearchBoxJsonLd } from 'next-seo';
import { OffertaType, PaginaType, MarcaVeicoloType } from '@next-nx/utils/types'
import { BRAND, DOMAIN, LOGO_URL } from '@next-nx/utils/settings';
import { getTimestamp } from '@next-nx/utils';
import styles from './HomeTemplate.module.sass'
import {
  BrandsSection,
  FaqHome,
  Hero,
  ImageTextBanner,
  NewsSection,
  NoleggioStepsHome,
  Page,
  ProdottiHome,
  StoreLocatorBanner,
  TrovaSoluzioneHome,
  VantaggiHome,
} from '@next-nx/shared-ui/organisms'
import { useFindMenu, useTealium } from '@next-nx/hooks'
import { useAllNewsQuery } from '@next-nx/shared-graphql/unipolrental'

interface Props {
  pagina: PaginaType
  offerteInEvidenza: OffertaType[]
  marcheVeicoli?: MarcaVeicoloType[]
}

const HomeTemplate = (props: Props) => {
  const { pagina, offerteInEvidenza, marcheVeicoli } = props

  const { data: newsData } = useAllNewsQuery({
    variables: { first: 7, orderBy: '-data_pubblicazione' },
  })
  const news = newsData?.allNews

  const homeMenu = useFindMenu('pagina-homepage')

  const heroMenu = useFindMenu('hero', homeMenu)
  const soluzioniMenu = useFindMenu('soluzioni', homeMenu)
  const offerteMenu = useFindMenu("offerte", homeMenu)
  const faqMenu = useFindMenu("faq", homeMenu)
  const faqBoxMenu = useFindMenu("faq-box", homeMenu)
  const vantaggiMenu = useFindMenu('vantaggi', homeMenu)
  const noleggioMenu = useFindMenu('noleggio', homeMenu)
  const noleggioBoxMenu = useFindMenu('noleggio-box', homeMenu)
  const marchiMenu = useFindMenu('marchi', homeMenu)
  const storelocatorMenu = useFindMenu("storelocator", homeMenu)
  const newsMenu = useFindMenu("news", homeMenu)

  const { linkDataLayer } = useTealium()

  const handleTracking = (action_type: string) => {
    const action_timestamp = getTimestamp() 
    
    linkDataLayer({
      virtual: 'action', 
      action_name: 'navigazione',  
      action_tech: 'click', 
      action_type: action_type?.replace(/\.|\/|\s+/g, '_').toLowerCase(),
      action_timestamp,
      action_effect: 'interazione_componente_nlt',
    }) 
  }

  return (
    <Page pagina={pagina} prefooter={false}>
      <div className={styles.homeTemplate}>
        <Hero item={heroMenu} home />
        <TrovaSoluzioneHome item={soluzioniMenu} className={styles.trovaSoluzione} />
        <ProdottiHome
          item={offerteMenu}
          offerte={offerteInEvidenza}
          className={styles.prodotti}
        />
        <FaqHome item={faqMenu} boxItem={faqBoxMenu} className={styles.faq} />
        <VantaggiHome item={vantaggiMenu} className={styles.vantaggi} />
        <NoleggioStepsHome item={noleggioMenu} className={styles.noleggioSteps} />
        <ImageTextBanner item={noleggioBoxMenu} className={styles.imageText} handleTracking={handleTracking} />
        <BrandsSection
          titolo={marchiMenu?.name}
          ctaLabel={marchiMenu?.ctaLabel}
          url={marchiMenu?.url}
          brands={marcheVeicoli}
          className={styles.brands}
        />
        <StoreLocatorBanner item={storelocatorMenu} className={styles.storelocator} />
        <NewsSection
          titolo={newsMenu?.name}
          ctaLabel={newsMenu?.ctaLabel}
          url={newsMenu?.url}
          news={news?.edges.map((x) => x?.node)}
          className={styles.brands}
        />
      </div>
      <OrganizationJsonLd 
        name={BRAND}
        url={DOMAIN}
        logo={LOGO_URL}
        contactPoints={[{
            telephone: "800 06 96 87",
            contactType: "Numero Verde",
            areaServed: "IT"
        }]} />
      <SiteLinksSearchBoxJsonLd
        url={DOMAIN}
        potentialActions={[{
            target: DOMAIN + '/ricerca?q',
            queryInput: 'search_term_string',
        }]} />
    </Page>
  )
}

export default HomeTemplate
